import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableRowProps,
  TablePagination,
  Checkbox,
  Button,
} from '@mui/material'
import { useRef, useState, useMemo, MouseEvent } from 'react'
import { HeadCell, Order, TableHeader, getComparator } from './TableHeader'
import styles from './DataTable.module.scss'

interface DataTableProps {
  tableHeaders: HeadCell[]
  tableRows: TableRowProps[]
  isLoading: boolean
  onClick?: (event: React.MouseEvent<HTMLTableRowElement>, row: any) => void
  selectable?: boolean
  isErrored?: boolean
  disableSorting?: boolean
  errorMessage?: string
  pagination?: boolean
  page?: number
  rowsPerPage?: number
  totalCount?: number
  initialOrder?: 'asc' | 'desc'
  onPageChange?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  rowsPerPageOptions?: number[]
  enableCheckboxes?: boolean
  selectedRows?: string[]
  onRowSelect?: (selectedRows: string[]) => void
  onExportAll?: () => void
  onExportSelected?: () => void
  onClearSelection?: () => void
  isExporting?: boolean
  disabledCheckboxIds?: string[]
  showExportButtons?: boolean
}

export const DataTable = ({
  tableHeaders,
  tableRows,
  isLoading,
  onClick,
  selectable,
  isErrored = false,
  disableSorting,
  errorMessage = 'Error loading data...please try again',
  pagination = false,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  rowsPerPageOptions = [5, 10, 25],
  enableCheckboxes = false,
  selectedRows = [],
  onRowSelect = () => {},
  onExportAll,
  onExportSelected,
  onClearSelection,
  isExporting = false,
  initialOrder = 'asc',
  disabledCheckboxIds = [],
  showExportButtons = false,
}: DataTableProps) => {
  // TODO: khanh to fix, ignoring console warning for now
  console.error = (message) => {
    if (
      typeof message === 'string' &&
      message.includes('<table> cannot appear as a descendant of <p>')
    ) {
      return
    }
  }

  const defaultKeyRef = useRef<keyof TableRowProps>('' as keyof TableRowProps)
  const [order, setOrder] = useState<Order>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof TableRowProps>(
    defaultKeyRef.current
  )

  useMemo(() => {
    const rows = tableRows[0] && Object.keys(tableRows[0])
    if (rows?.length > 0 && !disableSorting) {
      defaultKeyRef.current = rows[0] as keyof TableRowProps
    }
    setOrderBy(defaultKeyRef.current)
  }, [tableRows, disableSorting])

  const handleRequestSort = (
    event: MouseEvent<unknown>,
    property: keyof TableRowProps
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedRows = useMemo(
    () =>
      tableRows
        ?.slice()
        ?.sort((a, b) => getComparator(order, orderBy)(a as any, b as any)),
    [order, orderBy, tableRows]
  )

  const handleRowSelect = (row: TableRowProps) => {
    if (disabledCheckboxIds.includes((row as any).id)) {
      return
    }
    const rowId = (row as any).id
    const selectedIndex = selectedRows.indexOf(rowId)
    let newSelected: string[] = []

    if (selectedIndex === -1) {
      newSelected = [...selectedRows, rowId]
    } else {
      newSelected = selectedRows.filter((id) => id !== rowId)
    }

    onRowSelect && onRowSelect(newSelected)
  }

  const isSelected = (row: TableRowProps) => {
    const rowId = (row as any).id
    return selectedRows.includes(rowId)
  }

  const isCheckboxDisabled = (row: TableRowProps) => {
    return disabledCheckboxIds.includes((row as any).id)
  }

  return (
    <Box sx={{ width: '100%' }}>
      {(onExportAll || onExportSelected || onClearSelection) &&
        showExportButtons && (
          <Box
            sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'flex-end' }}
          >
            {selectedRows.length > 0 ? (
              <>
                {onExportSelected && (
                  <Button
                    variant="contained"
                    onClick={onExportSelected}
                    disabled={isExporting}
                    sx={{ textTransform: 'none' }}
                  >
                    {isExporting
                      ? `Exporting...`
                      : `Export (${selectedRows.length}) Record ${
                          selectedRows.length > 1 ? 's' : ''
                        }`}
                  </Button>
                )}
                {onClearSelection && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={onClearSelection}
                    disabled={isExporting}
                    sx={{
                      textTransform: 'none',
                    }}
                  >
                    Clear Selection
                  </Button>
                )}
              </>
            ) : (
              onExportAll && (
                <Button
                  variant="contained"
                  onClick={onExportAll}
                  disabled={isExporting}
                  sx={{
                    textTransform: 'none',
                    width: { xs: '100%', sm: '140px' },
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  {isExporting ? `Exporting...` : 'Export All'}
                </Button>
              )
            )}
          </Box>
        )}
      <TableContainer
        sx={{
          borderRadius: 2,
          mt: 0,
          boxShadow: 4,
        }}
      >
        <Table padding="normal" stickyHeader>
          <TableHeader
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            tableHeaders={tableHeaders}
            disableSorting={disableSorting}
            enableCheckboxes={enableCheckboxes}
            numSelected={selectedRows.length}
            rowCount={tableRows.length}
          />
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  align="center"
                  className={styles.loadingCell}
                  colSpan={tableHeaders.length}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : isErrored ? (
              <TableRow>
                <TableCell
                  className={styles.errorCell}
                  colSpan={tableHeaders.length}
                >
                  {errorMessage}
                </TableCell>
              </TableRow>
            ) : sortedRows.length > 0 ? (
              sortedRows.map((row, index) => {
                const isItemSelected = isSelected(row as TableRowProps)
                const rowData = row as TableRowProps
                return (
                  <TableRow
                    onClick={(event) => onClick && onClick(event, rowData)}
                    hover
                    tabIndex={-1}
                    key={rowData.id}
                    data-index={index}
                    className={
                      index % 2 === 0
                        ? `${styles.evenRow} ${
                            selectable
                              ? styles.selectableRow
                              : styles.defaultRow
                          }`
                        : `${styles.oddRow} ${
                            selectable
                              ? styles.selectableRow
                              : styles.defaultRow
                          }`
                    }
                    sx={{ cursor: selectable ? 'pointer' : 'default' }}
                  >
                    {enableCheckboxes && !isCheckboxDisabled(rowData) && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onClick={(event) => {
                            event.stopPropagation()
                            handleRowSelect(rowData)
                          }}
                        />
                      </TableCell>
                    )}
                    {enableCheckboxes && isCheckboxDisabled(rowData) && (
                      <TableCell padding="checkbox" />
                    )}
                    {tableHeaders.map((header, cellIndex) =>
                      header.hidden ? null : (
                        <TableCell
                          align="left"
                          key={cellIndex}
                          sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {(rowData as any)[header.id as string]}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell
                  align="center"
                  className={styles.noDataCell}
                  colSpan={tableHeaders.length}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {pagination && (
            <TableRow>
              <TablePagination
                colSpan={tableHeaders.length}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPageOptions={rowsPerPageOptions}
              />
            </TableRow>
          )}
        </Table>
      </TableContainer>
    </Box>
  )
}
